import React, { type ReactNode } from 'react';

import { createRoot, type Root } from 'react-dom/client';

import { type PlacementId } from '@schibsted-nmp/advertising-shared';

type SlotRoot = {
  root: Root;
  container: HTMLElement;
};

const slotRoots = new Map<PlacementId, SlotRoot>();
let renderCount = 0;

/**
 * Renders into one React root per placement, so a new search response replaces
 * the previous tree through React (running effect cleanups and disconnecting
 * observers) instead of orphaning it by wiping the DOM.
 *
 * Every call gets a fresh key: each response must remount the ad so its
 * per-instance impression tracking starts over.
 */
export function renderIntoSlot(
  placementId: PlacementId,
  slotElement: HTMLElement,
  element: ReactNode
) {
  renderCount += 1;
  const keyed = <React.Fragment key={renderCount}>{element}</React.Fragment>;

  const existing = slotRoots.get(placementId);
  if (existing && slotElement.contains(existing.container)) {
    existing.root.render(keyed);
    return;
  }

  // The layout replaced the slot element; the old root points at a detached node.
  existing?.root.unmount();

  const container = document.createElement('div');
  slotElement.replaceChildren(container);
  const root = createRoot(container);
  slotRoots.set(placementId, { root, container });
  root.render(keyed);
}

export function clearSlot(placementId: PlacementId, slotElement: HTMLElement) {
  slotRoots.get(placementId)?.root.unmount();
  slotRoots.delete(placementId);
  slotElement.replaceChildren();
}
